import { Injectable } from '@angular/core'
import { Clipboard } from '@capacitor/clipboard'
import { Platform } from '@ionic/angular'
import { Observable, Subject, Subscription } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import { PageLoadingContent } from 'src/app/common/components/page-loading/page-loading.component'
import { HomeCarouselListResponse, Notice, NoticeListResponse } from 'src/app/interfaces/main'
import { WebviewService } from 'src/app/services/webview/webview.service'
import { environment } from 'src/environments/environment'
import { isAdsenseBot } from 'src/utils/bot'
import { getFormattedDate } from 'src/utils/utils'
import { CustomHttpClient } from '../http/http.service'
import { LocaleService } from '../locale/locale-service'
import { SnackbarService } from '../snackbar/snackbar.service'
import { FeedCardListResponse } from './../../interfaces/main'
import { LogService } from './../log/log.service'
import { DeeplinkService } from './deeplink.service'

interface ShareData {
  files?: File[]
  text?: string
  title?: string
  url?: string
}

@Injectable({
  providedIn: 'root',
})
export class GeneralService {
  deeplink = new DeeplinkService(this, this.logService)

  noticeList: Notice[] = []

  private backbuttonPriority = 100
  private drawerSubject = new Subject<boolean>()
  private globalPageLoadingSubject = new Subject<{ showLoading: boolean; content?: PageLoadingContent }>()
  private appInstallPageLoadingSubject = new Subject<boolean>()
  private showCoupangAdSubject = new Subject<boolean>()
  private noticeListSubject = new Subject<Notice[]>()

  constructor(
    private platform: Platform,
    private http: CustomHttpClient,
    private snackbarService: SnackbarService,
    public logService: LogService,
    private webviewService: WebviewService,
    private localeSercive: LocaleService
  ) {}

  get isApp(): boolean {
    if (this.platform.is('desktop') || this.platform.is('mobileweb')) {
      return false
    } else {
      return true
    }
  }

  get isAndroid(): boolean {
    return this.isApp && this.platform.is('android')
  }

  get isIos(): boolean {
    return this.isApp && this.platform.is('ios')
  }

  get showCoupangAd(): boolean {
    if (isAdsenseBot) return false
    const clickedDt = localStorage.getItem('coupang_modal_click_dt')
    if (clickedDt) {
      const clicked = getFormattedDate(clickedDt, 'yyyy-MM-dd')
      const today = getFormattedDate(new Date().toISOString(), 'yyyy-MM-dd')
      return clicked !== today
    }
    return true
  }

  getNoticeListObservable(): Observable<Notice[]> {
    return this.noticeListSubject.asObservable()
  }

  getShowCoupangAdSubject(): Observable<boolean> {
    return this.showCoupangAdSubject.asObservable()
  }

  setCoupangModalClickedDt(): void {
    localStorage.setItem('coupang_modal_click_dt', new Date().toISOString())
    this.showCoupangAdSubject.next(this.showCoupangAd)
  }

  getRandomInt(min: number, max: number): number {
    min = Math.ceil(min)
    max = Math.floor(max)
    return Math.floor(Math.random() * (max - min)) + min //최댓값은 제외, 최솟값은 포함
  }

  getDrawerSubject(): Observable<boolean> {
    return this.drawerSubject.asObservable()
  }

  getGlobalPageLoadingSubject(): Observable<{ showLoading: boolean; content?: PageLoadingContent }> {
    return this.globalPageLoadingSubject.asObservable()
  }

  getAppInstallPageLoadingSubject(): Observable<boolean> {
    return this.appInstallPageLoadingSubject.asObservable()
  }

  openDrawer() {
    this.drawerSubject.next(true)
  }
  closeDrawer() {
    this.drawerSubject.next(false)
  }

  toggleGlobalPageLoading(showLoading: boolean, content?: PageLoadingContent): void {
    this.globalPageLoadingSubject.next({ showLoading, content })
  }

  toggleAppInstallPageLoading(showLoading: boolean): void {
    this.appInstallPageLoadingSubject.next(showLoading)
  }

  /**
   * 안드로이드 백버튼 구독 함수입니다.
   * 구독을 여러 페이지에서 할 수 있으나 priority가 높은 것으로 override됩니다.
   * **TODO - 푸시 알림으로 접속한 페이지에서는 hardware backbutton에 정의된 callback이
   *          subscribe되지 않는 에러가 존재
   */
  blockingBackButton(priority: number | undefined, callback: () => void | undefined): Subscription {
    return this.platform.backButton.subscribeWithPriority(priority ? priority : this.backbuttonPriority, () => {
      if (callback) {
        callback()
      }
    })
  }

  increasePriority(priority: number = 10) {
    this.backbuttonPriority += priority
  }

  getNoticeList(): Observable<NoticeListResponse> {
    return this.http.get<NoticeListResponse>(`${environment.apiUrl}/v1/notice`).pipe(
      tap((res: NoticeListResponse) => {
        this.noticeList = res?.data ?? []
        this.noticeListSubject.next(this.noticeList)
      })
    )
  }

  getFeedCardList(): Observable<FeedCardListResponse> {
    return this.http.get<FeedCardListResponse>(`${environment.apiUrl}/v1/feed/card`).pipe(
      map((res) => ({
        ...res,
        data: res.data.sort((a, b) => a.ordering - b.ordering),
      }))
    )
  }

  getHomeCarouselCardList(): Observable<HomeCarouselListResponse> {
    return this.http.get<HomeCarouselListResponse>(`${environment.apiUrl}/v2/feed/card`).pipe(
      map((res) => ({
        ...res,
        data: res.data.sort((a, b) => a.ordering - b.ordering),
      }))
    )
  }

  async openInappBrowser(url: string): Promise<void> {
    if (!url) {
      return
    }
    url = encodeURI(url)
    window.open(url, '_blank')
    this.logService.externalLinkLogging({ url })
  }

  isExternalLink(link: string): boolean {
    let result = false
    const { externalLink } = environment
    Object.keys(externalLink).forEach((key) => {
      if (externalLink[key] === link) {
        result = true
      }
    })
    return result
  }

  async copy(text: string): Promise<void> {
    if (this.webviewService.isWebView) {
      this.webviewService.postCopyClickedMessage(text)
    } else {
      await Clipboard.write({ string: text })
    }
  }

  navigateToStore(): void {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    // Check if the user agent string contains 'iPhone' or 'iPad'
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
    // Check if the user agent string contains 'Android'
    const isAndroid = /Android/i.test(navigator.userAgent)
    if (isMobile) {
      if (isAndroid) {
        this.openInappBrowser('market://details?id=com.woot.types')
      } else {
        this.openInappBrowser('itms-apps://itunes.apple.com/app/id1596043031')
      }
    } else {
      if (isAndroid) {
        this.openInappBrowser('https://play.google.com/store/apps/details?id=com.woot.types')
      } else {
        this.openInappBrowser('https://itunes.apple.com/kr/app/id1596043031')
      }
    }
  }

  openInstagram(): void {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    if (isMobile) {
      // Check if the user agent string contains 'iPhone' or 'iPad'
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
      // Check if the user agent string contains 'Android'
      const isAndroid = /Android/i.test(navigator.userAgent)
      const instagramStoryUrl = isIOS
        ? 'instagram://open'
        : `intent://instagram.com/#Intent;package=com.instagram.android;scheme=https;end`
      window.open(instagramStoryUrl, '_blank')
      return
    }
    this.snackbarService.openSnackBar(SnackbarService.CAN_USE_ONLY_APP)
  }

  async share(options: ShareData): Promise<boolean> {
    if (this.webviewService.isWebView) {
      this.webviewService.postWebShareMessage(options)
      return false
    }
    if (navigator.share === undefined) {
      return false
    }
    await navigator.share({
      title: options.title ?? options.text,
      url: options.url,
    })
    this.snackbarService.openSnackBar(SnackbarService.SHARE_LINK_SUCCEED)
    return true
  }
}
